import Pagination from '../../components/shared/pagination';
import React, { useEffect, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import { FiMoreVertical } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Modal from '../../components/shared/modal';
import ConfirmationModal from '../../components/shared/confirmation-modal';
import { statusColors } from '../../utils/status-color-utils';
import { RiArrowUpSLine } from 'react-icons/ri';
import { RxCaretSort } from 'react-icons/rx';
import Loader from '../../components/shared/loader';
import UpdateStatusModal from '../../components/shared/update-status-modal';
import { deleteVehicle, updateVehicleStatus } from '../../services/vehicles-service';
import placeholderCar from '../../assets/images/emptyImg.png';
import EditModal from './edit-modal';
import { camelCaseToWords } from '../../utils/structure-words-utils';
import { deleteJob, updateJobStatus } from '../../services/jobs-service';
import { formatUTCTimestampToLocal } from '../../utils/set-date-range-utils';
import { Tooltip } from 'react-tooltip';
import AuditLogModal from '../../components/shared/AuditLogModal';


const JobTabel = ({ data, setData, setSelectedRows, selectedRows, fetchGlobalData, newlyAddedItem, setNewlyAddedItem, onClearNewlyAddedItem, fetchJobs, loadingData, accountId, globalFilter, onGlobalFilterChange, jobStatuses, vehicleTypes, vehicleBodies, vehicleMakes, globalDataLoading }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sorting, setSorting] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAuditLogModalOpen, setIsAuditLogModalOpen] = useState(false);


  useEffect(() => {
    if (newlyAddedItem) {
      setSelectedRow(newlyAddedItem);
      setIsEditModalOpen(true);
      setNewlyAddedItem(null);
    }
  }, [newlyAddedItem, setNewlyAddedItem]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleActionClick = (id) => {
    setOpenMenuId(openMenuId === id ? null : id);
  };

  const closeMenu = () => {
    setOpenMenuId(null);
  };

  const handleRowClick = (row, e) => {
    if (!e.target.closest('input[type="checkbox"]')) {

      navigate(`/fsm/jobs/${row.original.jobId}`);
    }
  };

  const handleConfirmAction = async (action, row) => {
    setSelectedRow(row.original);
    if (action === 'Change Status') {
      setIsUpdateStatusModalOpen(true);
    } else if (action === 'edit') {
      setIsEditModalOpen(true);
      // if (vehicleTypes.length === 0 || vehicleBodies.length === 0 || vehicleMakes.length === 0) {
      //   await fetchGlobalData();
      // }
    } else {
      setConfirmAction(action);
      setIsConfirmModalOpen(true);
    }
    setErrorMessage(null);
    closeMenu();
  };

  const handleConfirm = async () => {
    if (!selectedRow || !confirmAction) return;

    if (confirmAction === 'delete') {
      try {
        setIsDeleting(true);
        const jobId = selectedRow.jobId;
        const res = await deleteJob(accountId, jobId);

        if (res.status === 204) {
          setData(prevData => prevData.filter(item => item.jobId !== jobId));
          setIsConfirmModalOpen(false);
          setSelectedRow(null);
          setConfirmAction(null);
          setSuccessMessage('job deleted successfully!');
        } else {
          setErrorMessage(res.data?.message || 'Failed to delete job');
        }
      } catch (error) {
        console.error('Error deleting job:', error);
        setErrorMessage('An error occurred while deleting the job');
      } finally {
        setIsDeleting(false);
      }
    } else if (confirmAction === 'deactivate') {
      // Placeholder: Implement deactivate logic if needed
      console.log('Deactivating job:', selectedRow);
      setIsConfirmModalOpen(false);
      setSelectedRow(null);
      setConfirmAction(null);
      setSuccessMessage('job deactivated successfully!');
    }
  };

  useEffect(() => {
    const handleClickOutside = () => closeMenu();
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const columns = useMemo(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <div className="flex justify-center w-full">
            <input
              type="checkbox"
              checked={table.getIsAllRowsSelected()}
              onChange={(e) => {
                e.stopPropagation();
                table.getToggleAllRowsSelectedHandler()(e);
              }}
              className="w-4 h-4"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center w-full" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={row.getIsSelected()}
              onChange={(e) => {
                e.stopPropagation();
                row.getToggleSelectedHandler()(e);
              }}
              className="w-4 h-4"
            />
          </div>
        ),
        enableSorting: false,
        size: 50,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: true,
        cell: info => camelCaseToWords(info.getValue()) || 'N/A',
      },
      {
        accessorKey: 'type',
        header: 'Type',
        enableSorting: true,
        cell: info => camelCaseToWords(info.getValue()) || 'N/A',
      },
      {
        accessorKey: 'location',
        header: 'Location',
        enableSorting: true,
        cell: info => {
          const location = info.getValue();
          if (!location) return 'N/A';
          const [lat, lng] = location.split(',');
          const formattedLat = parseFloat(lat).toFixed(3);
          const formattedLng = parseFloat(lng).toFixed(3);
          return `${formattedLat}, ${formattedLng}`;
        },
      },
      {
        accessorKey: 'startTime',
        header: 'Start Time',
        enableSorting: true,
        cell: info => formatUTCTimestampToLocal(info.getValue(), 'yyyy-MM-dd hh:mm a'),
      },
      {
        accessorKey: 'endTime',
        header: 'End Time',
        enableSorting: true,
        cell: info => formatUTCTimestampToLocal(info.getValue(), 'yyyy-MM-dd hh:mm a'),
      },

      {
        accessorKey: 'note',
        header: 'Note',
        enableSorting: false,
        cell: info => {
          const note = info.getValue() || '';
          const excerpt = note.length > 20 ? `${note.substring(0, 20)}...` : note;
          const tooltipId = `tooltip-${info.row.id}`;

          return (
            <>
              <span
                className="cursor-pointer whitespace-pre"
                data-tooltip-id={tooltipId}
                data-tooltip-content={note}
                data-tooltip-place="top"
              >
                {excerpt}
              </span>
              <Tooltip
                id={tooltipId}
                place="top"
                className="z-50"
                style={{
                  maxWidth: '250px',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  padding: '8px',
                  fontSize: '0.875rem',
                  backgroundColor: '#000d',
                  color: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                }}
              />

            </>
          );
        },
      },

      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: info => {
          const status = info.getValue()?.toLowerCase() || 'unknown';
          const color = statusColors[status] || statusColors.default;
          const displayStatus = camelCaseToWords(info.getValue());
          return (
            <div className="flex justify-start">
              <span
                className="py-0.5 rounded-md text-sm font-medium capitalize text-center whitespace-nowrap"
                style={{
                  backgroundColor: color,
                  color: 'black',
                  border: `1px solid ${color}`,
                  width: '100px',
                  display: 'inline-block',
                  textAlign: 'center'
                }}
              >
                {displayStatus}
              </span>
            </div>
          );
        },
      },

      {
        id: 'actions',
        header: 'Action',
        cell: ({ row }) => {
          const rowId = row.id;

          return (
            <div className="relative flex justify-center w-full">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(rowId);
                }}
                className="p-1 rounded-full hover:bg-gray-200"
              >
                <FiMoreVertical className="text-gray-600 text-sm" />
              </button>
              {openMenuId === rowId && (
                <div
                  className={`absolute right-0 w-32 bg-white rounded-md shadow-lg z-10 border border-gray-200 top-5`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => handleConfirmAction('Change Status', row)}
                  >
                    Change Status
                  </button>
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => handleConfirmAction('edit', row)}
                  >
                    Edit
                  </button>
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      setSelectedRow(row.original);
                      setIsAuditLogModalOpen(true);
                      closeMenu();
                    }}
                  >
                    Audit Log
                  </button>
                  <button
                    className="block w-full text-left px-4 py-1 text-sm text-red-600 hover:bg-gray-100"
                    onClick={() => handleConfirmAction('delete', row)}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          );
        },
        enableSorting: false,
        size: 50,
      },
    ],
    [openMenuId]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      rowSelection: selectedRows,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    enableSortingRemoval: false,
    onRowSelectionChange: setSelectedRows,
    globalFilterFn: (row, columnId, filterValue) => {
      const searchLower = filterValue.toLowerCase();
      const rowValue = String(row.getValue(columnId)).toLowerCase();

      if (columnId === 'startTime' || columnId === 'endTime') {
        return formatUTCTimestampToLocal(rowValue, 'yyyy-MM-dd hh:mm a').toLowerCase().includes(searchLower);
      } else if (columnId.includes('.')) {
        const [parent, child] = columnId.split('.');
        const value = row.original[parent]?.[child] || '';
        return String(value).toLowerCase().includes(searchLower);
      }

      return rowValue.includes(searchLower);
    },
  });

  return (
    <div className="w-full">
      {successMessage && (
        <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-md text-sm text-center">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="mb-4 px-4 py-2 bg-red-100 text-red-800 rounded-md text-sm text-center">
          {errorMessage}
        </div>
      )}

      <div className="overflow-x-auto max-w-full pb-[105px]">
        {loadingData ? (
          <Loader />
        ) : (
          <table className="min-w-full text-sm border border-gray-200">
            <colgroup>
              <col style={{ width: '50px' }} />
              <col style={{ minWidth: '120px' }} />
              <col style={{ minWidth: '120px' }} />
              <col style={{ minWidth: '100px' }} />
              <col style={{ minWidth: '120px' }} />
              <col style={{ minWidth: '120px' }} />
              <col style={{ minWidth: '120px' }} />
              <col style={{ minWidth: '120px' }} />
              <col style={{ width: '50px' }} />
            </colgroup>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="bg-gray-100 whitespace-nowrap capitalize text-left">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-2 py-1.5 border-b capitalize"
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        width: header.column.getSize(),
                      }}
                    >
                      <div className="flex items-center gap-1">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span className="text-black">
                            {header.column.getIsSorted() === 'asc' ? (
                              <RiArrowUpSLine className="text-base mt-0.5" />
                            ) : header.column.getIsSorted() === 'desc' ? (
                              <RiArrowUpSLine className="text-base rotate-180" />
                            ) : (
                              <RxCaretSort className="text-lg" />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table
                .getRowModel()
                .rows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                .map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={(e) => handleRowClick(row, e)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-2 py-1 border-b whitespace-nowrap"
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={isUpdateStatusModalOpen && selectedRow}
        onClose={() => {
          setIsUpdateStatusModalOpen(false);
          setSelectedRow(null);
        }}
        modalClass={'xl:!w-[25%] lg:!w-[30%] md:!w-[35%] sm:!w-[45%] !w-[90%] !px-0'}
      >
        <UpdateStatusModal
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          setData={setData}
          setIsUpdateStatusModalOpen={setIsUpdateStatusModalOpen}
          fetchData={fetchJobs}
          accountId={accountId}
          modalTitle='Change Job Status'
          statusKey='status'
          statusOptions={jobStatuses}
          uniqueIdKey='jobId'
          updateStatusApi={updateJobStatus}
        />
      </Modal>

      <Modal
        isOpen={isConfirmModalOpen && selectedRow}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setErrorMessage(null);
          setSelectedRow(null);
        }}
        modalClass={'xl:!w-[25%] lg:w-[32%] md:w-[40%] sm:w-[60%] w-[90%]'}
      >
        <ConfirmationModal
          confirmAction={confirmAction}
          setIsConfirmModalOpen={setIsConfirmModalOpen}
          handleConfirm={handleConfirm}
          loading={isDeleting}
          errorMessage={errorMessage}
        />
      </Modal>

      <Modal
        isOpen={isEditModalOpen && selectedRow}
        onClose={() => {
          setIsEditModalOpen(false);
          setErrorMessage(null);
          setSelectedRow(null);
        }}
        modalClass={'xl:w-[40%] lg:w-[45%] md:w-[55%] sm:w-[70%] w-[90%] '}
      >
        <EditModal
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          setIsEditModalOpen={setIsEditModalOpen}
          setData={setData}
          globalDataLoading={globalDataLoading}
        />
      </Modal>

      <Modal
        isOpen={isAuditLogModalOpen && selectedRow}
        onClose={() => {
          setIsAuditLogModalOpen(false);
          setSelectedRow(null);
        }}
        modalClass="xl:w-[60%] lg:w-[70%] md:w-[80%] sm:w-[90%] w-[95%]"
      >
        <AuditLogModal
          isOpen={isAuditLogModalOpen}
          onClose={() => {
            setIsAuditLogModalOpen(false);
            setSelectedRow(null);
          }}
          accountId={accountId}
          linkedType="job"
          linkedId={selectedRow?.jobId}
        />
      </Modal>

      <Pagination
        currentPage={currentPage}
        totalItems={table.getFilteredRowModel().rows.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(count) => {
          setItemsPerPage(count);
          setCurrentPage(1);
        }}
      />
    </div >
  );
};

export default JobTabel;